import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../utils/apiConfig';
import { auth, db } from '../services/firebase';
import { collection, onSnapshot, query, orderBy, where, getDocs, doc, updateDoc, deleteDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import {
    Box, Typography, Card, CardContent, Button, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, Chip, IconButton, Dialog,
    DialogTitle, DialogContent, DialogActions, Grid, TextField, Collapse, Divider
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import styles from '../styles/Dashboard.module.css';
import { format } from 'date-fns';

const QuoteRow = ({ quote, onToggleStatus, onSavePrices, onDelete }) => {
    const [open, setOpen] = useState(false);
    // Local state for items to allow editing prices without saving immediately ?? 
    // Actually, let's edit directly on the displayed list if we pass a handler?
    // Or better: Local state in the Row for the editing session.
    const [localItems, setLocalItems] = useState(quote.itens || []);
    const [negotiateDialogOpen, setNegotiateDialogOpen] = useState(false);
    const [negotiateMessage, setNegotiateMessage] = useState('');

    const handleConfirmNegotiation = () => {
        // Change Status
        onToggleStatus(quote.id, 'em_negociacao');
        setNegotiateDialogOpen(false);

        // Open WhatsApp
        const phone = quote.fornecedor_telefone ? quote.fornecedor_telefone.replace(/\D/g, '') : '';
        if (phone) {
            const link = `https://predioo.com/cotacao/${quote.token}`; // Assuming quote has token.
            const text = `Olá, sobre a cotação *${quote.titulo}*.\n\n${negotiateMessage}\n\nPor favor, atualize os valores no link: ${link}`;
            const url = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
            window.open(url, '_blank');
        } else {
            alert("Fornecedor sem telefone cadastrado.");
        }
    };

    useEffect(() => {
        setLocalItems(quote.itens || []);
    }, [quote.itens]);

    const totalItens = localItems.reduce((acc, item) => acc + (parseFloat(item.quantidade) || 0), 0);
    const totalValor = localItems.reduce((acc, item) => acc + ((parseFloat(item.quantidade) || 0) * (parseFloat(item.preco_unitario) || 0)), 0);

    // Group for Consolidated View (Price Entry)
    // Key: nome + unit
    const consolidated = {};
    localItems.forEach((item, index) => {
        const key = `${item.nome}-${item.unidade}`;
        if (!consolidated[key]) {
            consolidated[key] = {
                key,
                nome: item.nome,
                unidade: item.unidade,
                totalQty: 0,
                price: item.preco_unitario || 0, // Assume same price for same item
                indices: [] // Track which items in the original array belong here
            };
        }
        consolidated[key].totalQty += (parseFloat(item.quantidade) || 0);
        consolidated[key].indices.push(index);
        // If prices differ (unlikely if mass update), we might take the first one or max. 
        // We will enforce sync when editing.
    });

    const handlePriceChange = (key, newPrice) => {
        const val = parseFloat(newPrice);
        const updatedItems = [...localItems];
        consolidated[key].indices.forEach(idx => {
            updatedItems[idx].preco_unitario = val;
        });
        setLocalItems(updatedItems);
    };

    const handleSave = () => {
        onSavePrices(quote.id, localItems);
    };

    // Calculate Rateio (Distribution by Condo)
    const rateio = {};
    localItems.forEach(item => {
        const cid = item.condominio_id || 'unknown';
        const cName = item.condominio_nome || 'Desconhecido';
        if (!rateio[cid]) {
            rateio[cid] = { name: cName, total: 0, itens: 0 };
        }
        rateio[cid].itens += (parseFloat(item.quantidade) || 0);
        rateio[cid].total += ((parseFloat(item.quantidade) || 0) * (parseFloat(item.preco_unitario) || 0));
    });

    return (
        <>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' }, backgroundColor: open ? '#f5f5f5' : 'inherit' }}>
                <TableCell>
                    <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell component="th" scope="row">
                    {format(quote.data_criacao?.toDate() || new Date(), 'dd/MM/yyyy HH:mm')}
                </TableCell>
                <TableCell>{quote.titulo || 'Sem Título'}</TableCell>
                <TableCell>{quote.fornecedor_nome || '-'}</TableCell>
                <TableCell>
                    <Chip
                        label={quote.status.toUpperCase().replace('_', ' ')}
                        color={
                            quote.status === 'aprovada' ? 'success' :
                                quote.status === 'cancelada' ? 'error' :
                                    quote.status === 'aguardando_resposta' ? 'info' :
                                        'warning'
                        }
                        size="small"
                    />
                </TableCell>
                <TableCell align="right">
                    <Box>
                        <Typography variant="body2" fontWeight="bold">R$ {totalValor.toFixed(2)}</Typography>
                        <Typography variant="caption">{totalItens} itens</Typography>
                    </Box>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2 }}>

                            <Grid container spacing={4}>
                                {/* LEFT: Consolidated Price Input */}
                                <Grid item xs={12} md={7}>
                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                        1. Entrada de Preços (Unitários)
                                    </Typography>
                                    <Table size="small" aria-label="prices">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Item</TableCell>
                                                <TableCell align="right">Qtd Total</TableCell>
                                                <TableCell align="right" width={150}>Preço Unit. (R$)</TableCell>
                                                <TableCell align="right">Subtotal</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {Object.values(consolidated).map((group) => (
                                                <TableRow key={group.key}>
                                                    <TableCell>{group.nome}</TableCell>
                                                    <TableCell align="right">{group.totalQty} {group.unidade}</TableCell>
                                                    <TableCell align="right">
                                                        <TextField
                                                            size="small"
                                                            type="number"
                                                            value={group.price}
                                                            onChange={(e) => handlePriceChange(group.key, e.target.value)}
                                                            disabled={quote.status !== 'aguardando_resposta' && quote.status !== 'em_negociacao'}
                                                            InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        R$ {(group.totalQty * group.price).toFixed(2)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>

                                    {(quote.status === 'aguardando_resposta' || quote.status === 'em_negociacao') && (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            sx={{ mt: 2 }}
                                            onClick={handleSave}
                                        >
                                            Salvar Preços
                                        </Button>
                                    )}
                                </Grid>

                                {/* RIGHT: Rateio Distribution */}
                                <Grid item xs={12} md={5}>
                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                        2. Rateio por Condomínio
                                    </Typography>
                                    <Paper variant="outlined" sx={{ p: 0 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Condomínio</TableCell>
                                                    <TableCell align="right">Itens</TableCell>
                                                    <TableCell align="right">Total a Pagar</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {Object.values(rateio).map((r, i) => (
                                                    <TableRow key={i}>
                                                        <TableCell component="th" scope="row">{r.name}</TableCell>
                                                        <TableCell align="right">{r.itens}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                                            R$ {r.total.toFixed(2)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                <TableRow sx={{ backgroundColor: '#eeeeee' }}>
                                                    <TableCell fontWeight="bold">TOTAL GERAL</TableCell>
                                                    <TableCell align="right">{totalItens}</TableCell>
                                                    <TableCell align="right" fontWeight="bold">R$ {totalValor.toFixed(2)}</TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </Paper>

                                    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Alterar Status:</Typography>
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                            {quote.status !== 'cancelada' && (
                                                <>
                                                    {quote.status === 'enviada' && (
                                                        <Button size="small" variant="outlined" color="info" onClick={() => onToggleStatus(quote.id, 'aguardando_resposta')}>
                                                            Marcar como Respondida (Manual)
                                                        </Button>
                                                    )}

                                                    {(quote.status === 'aguardando_aprovacao' || quote.status === 'aguardando_resposta' || quote.status === 'em_negociacao') && (
                                                        <Button size="small" variant="contained" color="success" onClick={() => onToggleStatus(quote.id, 'aprovada')}>
                                                            Aprovar Compra
                                                        </Button>
                                                    )}

                                                    {quote.status === 'aguardando_aprovacao' && (
                                                        <Button size="small" variant="contained" color="warning" onClick={() => setNegotiateDialogOpen(true)}>
                                                            Negociar
                                                        </Button>
                                                    )}

                                                    {quote.status !== 'aprovada' && (
                                                        <Button size="small" variant="outlined" color="error" onClick={() => onToggleStatus(quote.id, 'cancelada')}>
                                                            Cancelar
                                                        </Button>
                                                    )}
                                                </>
                                            )}
                                            {quote.status === 'cancelada' && (
                                                <Button size="small" variant="outlined" color="error" onClick={() => onDelete(quote.id)}>
                                                    Excluir
                                                </Button>
                                            )}
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>

                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
            {/* Dialog de Negociação */}
            <Dialog open={negotiateDialogOpen} onClose={() => setNegotiateDialogOpen(false)}>
                <DialogTitle>Negociar Cotação</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Descreva o motivo da negociação ou solicite um desconto. Isso será enviado por WhatsApp.
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Mensagem para o Fornecedor"
                        fullWidth
                        multiline
                        rows={3}
                        value={negotiateMessage}
                        onChange={(e) => setNegotiateMessage(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNegotiateDialogOpen(false)}>Cancelar</Button>
                    <Button onClick={handleConfirmNegotiation} variant="contained" color="primary">
                        Confirmar e Enviar Zap
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const GerenciarCotacoes = () => {
    const [quotes, setQuotes] = useState([]);
    const [userRole, setUserRole] = useState('');

    useEffect(() => {
        const unsubscribeAuth = auth.onAuthStateChanged(async (user) => {
            if (user) {
                const userDoc = await getDocs(query(collection(db, 'tb_usuarios'), where('__name__', '==', user.uid)));
                if (!userDoc.empty) {
                    const role = userDoc.docs[0].data().papel;
                    setUserRole(role);
                    fetchQuotes(role, user.uid);
                }
            }
        });
        return () => unsubscribeAuth();
    }, []);

    const fetchQuotes = (role, uid) => {
        let q;
        // Se for Administradora, vê as cotações que ELA criou.
        // Se for Admin Sistema, vê tudo.
        // Se for Síndico, vê as cotações que tenham itens do condomínio dele? (Futuro). 
        // Por enquanto vamos focar na Administradora.

        if (role === 'administradora') {
            q = query(collection(db, 'tb_cotacoes'), where('administradora_id', '==', uid), orderBy('data_criacao', 'desc'));
        } else if (role === 'admin_sistema') {
            q = query(collection(db, 'tb_cotacoes'), orderBy('data_criacao', 'desc'));
        } else if (role === 'sindico') {
            // Sindico should see quotes they created (or quotes for their condo, but we use author_uid for simplicity as they create them)
            q = query(collection(db, 'tb_cotacoes'), where('author_uid', '==', uid), orderBy('data_criacao', 'desc'));
        } else {
            // Outros roles por enquanto nao veem ou veem filtrado
            setQuotes([]);
            return;
        }

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setQuotes(list);
        });
        return unsubscribe;
    };

    const handleSavePrices = async (quoteId, updatedItems) => {
        try {
            await updateDoc(doc(db, 'tb_cotacoes', quoteId), {
                itens: updatedItems
            });
            alert("Preços salvos com sucesso!");
        } catch (error) {
            console.error("Erro ao salvar preços", error);
            alert("Erro ao salvar.");
        }
    };

    const handleToggleStatus = async (quoteId, newStatus) => {
        try {
            await updateDoc(doc(db, 'tb_cotacoes', quoteId), {
                status: newStatus
            });

            if (newStatus === 'aprovada') {
                const quote = quotes.find(q => q.id === quoteId);
                if (quote) {
                    // 1. Create Purchase Order (Ordem de Compra)
                    try {
                        const orderRef = await addDoc(collection(db, 'tb_ordens_compra'), {
                            cotacao_id: quoteId,
                            cotacao_titulo: quote.titulo,
                            cotacao_token: quote.token,
                            fornecedor_id: quote.fornecedor_id,
                            fornecedor_nome: quote.fornecedor_nome,
                            status: 'pendente',
                            data_criacao: serverTimestamp(),
                            itens: quote.itens // Save snapshot of items at approval time
                        });

                        // 2. Open WhatsApp (Send Order)
                        const phone = quote.fornecedor_telefone ? quote.fornecedor_telefone.replace(/\D/g, '') : '';
                        if (phone) {
                            const text = `*PEDIDO APROVADO!* ✅\n\nCotação: ${quote.titulo}\nOrdem de Compra gerada: #${orderRef.id.slice(0, 5)}\n\nFavor proceder com a entrega/faturamento.\n\nDetalhes no sistema: https://predioo.com/cotacao/${quote.token}`;
                            const url = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
                            window.open(url, '_blank');
                        } else {
                            alert("Cotação aprovada e Ordem de Compra gerada! (Sem telefone para WhatsApp)");
                        }
                    } catch (err) {
                        console.error("Erro ao criar ordem de compra", err);
                        alert("Cotação aprovada, mas houve erro ao gerar a Ordem de Compra.");
                    }
                }
            }
        } catch (error) {
            console.error("Erro ao atualizar status", error);
            alert("Erro ao atualizar status.");
        }
    };

    const handleDelete = async (quoteId) => {
        if (!window.confirm("Tem certeza que deseja excluir esta cotação cancelada?")) return;
        try {
            await deleteDoc(doc(db, 'tb_cotacoes', quoteId));
        } catch (error) {
            console.error("Erro ao excluir cotação", error);
            alert("Erro ao excluir cotação.");
        }
    };



    return (
        <Box>
            <Box component="main" sx={{ flexGrow: 1, p: 3, background: 'linear-gradient(45deg, #1a237e 30%, #283593 90%)', minHeight: '100vh' }}>
                <Typography variant="h4" gutterBottom sx={{ color: 'white' }}>Cotações</Typography>

                <Card className={styles.card}>
                    <CardContent>
                        <TableContainer component={Paper}>
                            <Table aria-label="collapsible table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell />
                                        <TableCell>Data</TableCell>
                                        <TableCell>Título</TableCell>
                                        <TableCell>Fornecedor</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right">Resumo</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {quotes.map((quote) => (
                                        <QuoteRow key={quote.id} quote={quote} onToggleStatus={handleToggleStatus} onSavePrices={handleSavePrices} onDelete={handleDelete} />
                                    ))}
                                    {quotes.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center">Nenhuma cotação encontrada.</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default GerenciarCotacoes;
